/**
 * ModalPortal
 *
 * Shared shell for the dialogs opened from the account menu (Settings, Get
 * help, Log out). Handles the dark backdrop, closing on Escape or a click
 * outside, and locking the page behind it from scrolling.
 *
 * Why a "portal": these dialogs are opened from inside the sidebar, and the
 * sidebar slides in and out using a CSS transform. A transform makes an
 * element the reference point for anything positioned inside it — so a
 * normal full-screen dialog would be trapped inside the sidebar's narrow
 * column instead of covering the page. A portal renders the dialog at the
 * top level of the page instead, escaping that entirely.
 *
 * Props:
 *   onClose     — called on backdrop click, Escape, or the close button
 *   closable    — false while a request is in flight, so the user can't walk
 *                 away unsure whether it went through (default true)
 *   width       — max width of the panel in pixels (default 520)
 *   labelledBy  — id of the element that titles the dialog, for screen readers
 *   children    — the panel's contents
 */

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { useContentOffset } from '../context/SidebarContext'
import { IS_NATIVE_APP } from '../services/runtime'

export default function ModalPortal({ onClose, closable = true, width = 520, labelledBy, children }) {
  const contentOffset = useContentOffset()

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && closable) onClose() }
    window.addEventListener('keydown', onKey)

    // Stop the page underneath scrolling while the dialog is open.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [closable, onClose])

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && closable) onClose() }}
      style={{
        // left tracks the sidebar so the backdrop only spans the content
        // pane — a plain `inset: 0` centres in the raw viewport, which sits
        // half a sidebar-width left of where the page underneath is
        // actually centred.
        position: 'fixed', top: 0, right: 0, bottom: 0, left: contentOffset, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        background: 'rgba(4,18,46,0.55)',
        backdropFilter: 'blur(6px)',
        fontFamily: JAI_FONTS.tight,
        transition: 'left 220ms cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        style={{
          width: '100%', maxWidth: width,
          // Plain vh on native can run taller than what's actually reachable
          // once the notch/home-indicator safe areas are accounted for,
          // pushing the dialog's bottom past where a finger can scroll it
          // into view. dvh plus explicit safe-area subtraction keeps the
          // whole dialog within the true visible frame. Web keeps 88vh.
          maxHeight: IS_NATIVE_APP
            ? 'calc(100dvh - var(--safe-top) - var(--safe-bottom) - 32px)'
            : '88vh',
          display: 'flex', flexDirection: 'column',
          background: 'white',
          borderRadius: 16,
          border: `1px solid ${JAI_COLORS.hair}`,
          boxShadow: '0 24px 70px rgba(4,18,46,0.38)',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

// ── Shared bits the account-menu dialogs all use ────────────────────────────

/** Title bar with an optional close button. */
export function ModalHeader({ title, titleId, onClose, closable = true }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '18px 22px', flexShrink: 0,
        borderBottom: `1px solid ${JAI_COLORS.hair}`,
      }}
    >
      <h2 id={titleId} style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: JAI_COLORS.navy }}>
        {title}
      </h2>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          disabled={!closable}
          title="Close"
          aria-label="Close"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 30, height: 30, borderRadius: 8, border: 'none',
            background: 'transparent', color: JAI_COLORS.mute,
            cursor: closable ? 'pointer' : 'not-allowed',
            opacity: closable ? 1 : 0.4,
            transition: 'background 150ms, color 150ms',
          }}
          onMouseEnter={(e) => { if (closable) { e.currentTarget.style.background = JAI_COLORS.paper; e.currentTarget.style.color = JAI_COLORS.navy } }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = JAI_COLORS.mute }}
        >
          <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
            <path d="m13 5-8 8M5 5l8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}

/** Secondary (outline) button — Cancel, Close, and similar. */
export function GhostButton({ children, onClick, disabled, type = 'button', full = false }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: full ? 1 : 'initial',
        padding: '10px 18px', borderRadius: 10,
        fontFamily: JAI_FONTS.tight, fontSize: 14, fontWeight: 600,
        color: JAI_COLORS.navy, background: 'white',
        border: `1px solid ${JAI_COLORS.hair}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 150ms',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = JAI_COLORS.paper }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'white' }}
    >
      {children}
    </button>
  )
}

/** Solid action button. `tone="danger"` for destructive confirmations. */
export function SolidButton({ children, onClick, disabled, loading, type = 'button', tone = 'brand', full = false }) {
  const off = disabled || loading
  const background = tone === 'danger'
    ? (off ? 'rgba(214,58,58,0.5)' : JAI_COLORS.error)
    : (off ? 'rgba(30,91,214,0.45)' : `linear-gradient(135deg, ${JAI_COLORS.blueBright} 0%, ${JAI_COLORS.blue} 55%, #1A4FBF 100%)`)

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={off}
      style={{
        flex: full ? 1 : 'initial',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '10px 18px', borderRadius: 10, border: 'none',
        fontFamily: JAI_FONTS.tight, fontSize: 14, fontWeight: 650,
        color: 'white', background,
        cursor: off ? 'not-allowed' : 'pointer',
        transition: 'transform 150ms, box-shadow 150ms',
        boxShadow: off ? 'none' : (tone === 'danger' ? '0 6px 18px rgba(214,58,58,0.28)' : '0 6px 18px rgba(30,91,214,0.30)'),
      }}
      onMouseEnter={(e) => { if (!off) e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}
    >
      {loading && (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ animation: 'jaiSpin 0.7s linear infinite' }}>
          <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  )
}

/** Footer strip holding the dialog's buttons. */
export function ModalFooter({ children }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
        padding: '14px 22px', flexShrink: 0,
        borderTop: `1px solid ${JAI_COLORS.hair}`,
        background: JAI_COLORS.paper,
      }}
    >
      {children}
    </div>
  )
}
